import { ReactElement, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// project imports
import useAuth from 'hooks/useAuth';
import useCompanyStatus from 'hooks/useCompanyStatus';
import Loader from 'components/Loader';

// ==============================|| COMPANY GUARD ||============================== //

interface CompanyGuardProps {
  children: ReactElement;
  requiresCompany?: boolean; // true = needs company, false = needs no company
}

export default function CompanyGuard({ children, requiresCompany = true }: CompanyGuardProps) {
  const { isLoggedIn } = useAuth();
  const { hasCompany, loading } = useCompanyStatus();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn) {
      return; // AuthGuard will handle this
    }

    if (!loading) {
      if (requiresCompany && !hasCompany) {
        // User needs company but doesn't have one - redirect to company selection
        navigate('/company-selection', { replace: true });
      } else if (!requiresCompany && hasCompany) {
        // User has company but trying to access company selection - redirect to dashboard
        navigate('/apps/quote/dashboard', { replace: true });
      }
    }
  }, [isLoggedIn, hasCompany, loading, requiresCompany, navigate]);

  if (loading) {
    return <Loader />;
  }

  // Show content if:
  // - User needs company and has company, OR
  // - User doesn't need company and doesn't have company
  if ((requiresCompany && hasCompany) || (!requiresCompany && !hasCompany)) {
    return children;
  }

  return <Loader />; // Show loader while redirecting
}
